import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useWallet } from '../context/WalletContext';
import { useUserData } from '../hooks/useUserData';
import { useContracts } from '../hooks/useContracts';
import { useTeamStats } from '../hooks/useTeamStats';
import { useLevelMembers } from '../hooks/useLevelMembers';
import { fmtUSD, truncateAddress, bpsToPct } from '../utils/format';
import { rankTag, rankLabel } from '../utils/constants';
import GlassCard from '../components/common/GlassCard';
import CopyButton from '../components/common/CopyButton';
import ConnectPrompt from '../components/common/ConnectPrompt';
import AtomSpinner from '../components/loader/AtomSpinner';
import Modal from '../components/common/Modal';
import Icon from '../components/common/Icon';
import NeonButton from '../components/common/NeonButton';

const PAGE = 20;

const Team = () => {
  const { account } = useWallet();
  const c = useContracts();
  const { summary, userId, loading } = useUserData();
  const team = useTeamStats();

  const [tab, setTab] = useState('directs');
  const [levels, setLevels] = useState([]);
  const [legs, setLegs] = useState([]);
  const [levelEarned, setLevelEarned] = useState([]);
  const [loadingExtra, setLoadingExtra] = useState(true);

  // Level members drill-down
  const [openLevel, setOpenLevel] = useState(null); // 1..30 or null

  useEffect(() => {
    let mounted = true;
    (async () => {
      if (!userId || userId === 0n) { setLoadingExtra(false); return; }
      try {
        const [lv, lg, le] = await Promise.all([
          c.read.views.getLevelStats(userId),
          c.read.views.getUserLegs(userId),
          c.read.views.getLevelEarnedByLevel(userId).catch(() => new Array(30).fill(0n)),
        ]);
        if (!mounted) return;
        setLevels(lv);
        setLegs(lg);
        setLevelEarned(le);
      } catch {}
      if (mounted) setLoadingExtra(false);
    })();
    return () => { mounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  if (!account) return <ConnectPrompt />;

  const refLink = typeof window !== 'undefined'
    ? `${window.location.origin}${window.location.pathname}#/register?ref=${userId || ''}`
    : '';

  return (
    <div className="animate-fade-up">
      <div className="page-title">Team & Referrals</div>
      <div className="page-subtitle">Grow your network — earn level and rank income from downline farming.</div>

      {summary?.active ? (
        <div className="ref-block">
          <div className="rb-icon"><Icon name="team" size={20} /></div>
          <div className="rb-body">
            <div className="rb-label">Your Referral Link · ID #{String(userId || 0)}</div>
            <div className="rb-url">{refLink}</div>
          </div>
          <CopyButton value={refLink} label="Copy" />
        </div>
      ) : (
        <div className="alert a-warn" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 20 }}>🔒</span>
          <span>
            <strong>Referral link locked.</strong>{' '}
            <Link to="/invest" style={{ color: 'var(--grx-neon)', textDecoration: 'underline' }}>Make your first investment</Link>
            {' '}to activate your account and start referring.
          </span>
        </div>
      )}

      <div className="team-grid mb-4">
        <div className="team-tile">
          <div className="tt-icon"><Icon name="users" size={20} /></div>
          <div className="tt-label">Total Team</div>
          <div className="tt-value neon">{String(team.totalTeam)}</div>
          <div className="tt-sub">across 30 levels</div>
        </div>
        <div className="team-tile">
          <div className="tt-icon"><Icon name="userPlus" size={20} /></div>
          <div className="tt-label">Direct Referrals</div>
          <div className="tt-value">{String(team.directCount)}</div>
          <div className="tt-sub">L1 referrals</div>
        </div>
        <div className="team-tile">
          <div className="tt-icon cyan"><Icon name="briefcase" size={20} /></div>
          <div className="tt-label">Total Business</div>
          <div className="tt-value cyan">{fmtUSD(team.totalBiz)}</div>
          <div className="tt-sub">tree-wide volume</div>
        </div>
        <div className="team-tile">
          <div className="tt-icon"><Icon name="dollar" size={20} /></div>
          <div className="tt-label">Direct Business</div>
          <div className="tt-value">{fmtUSD(team.directBiz)}</div>
          <div className="tt-sub">L1 leg total</div>
        </div>
        <div className="team-tile">
          <div className="tt-icon"><Icon name="chartBar" size={20} /></div>
          <div className="tt-label">Self Invested</div>
          <div className="tt-value">{fmtUSD(summary?.totalInvestmentUSDT)}</div>
          <div className="tt-sub">lifetime</div>
        </div>
        <div className="team-tile">
          <div className="tt-icon cyan"><Icon name="trendingUp" size={20} /></div>
          <div className="tt-label">Total Earned</div>
          <div className="tt-value cyan">{fmtUSD(summary?.totalEarnedUSDT)}</div>
          <div className="tt-sub">lifetime rewards</div>
        </div>
      </div>

      <div className="tabs">
        <button className={'tab' + (tab === 'directs' ? ' active' : '')} onClick={() => setTab('directs')}>
          Direct Legs <span className="tab-count">{legs.length}</span>
        </button>
        <button className={'tab' + (tab === 'levels' ? ' active' : '')} onClick={() => setTab('levels')}>
          Level Tree (30)
        </button>
      </div>

      {tab === 'directs' && (
        <GlassCard title="Direct Referral Legs">
          {loading || loadingExtra ? (
            <div className="empty-state"><AtomSpinner size="lg" /></div>
          ) : legs.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">⊕</div>
              <div className="empty-title">No direct referrals yet</div>
              <div className="mt-2">Share your referral link above to grow your team.</div>
            </div>
          ) : (
            <div className="table-wrap">
              <table className="grx-table">
                <thead>
                  <tr><th>ID</th><th>Wallet</th><th>Leg Business</th></tr>
                </thead>
                <tbody>
                  {legs.map((l, i) => (
                    <tr key={i}>
                      <td className="mono">#{String(l.directUserId)}</td>
                      <td className="mono">{truncateAddress(l.wallet)}</td>
                      <td className="mono neon">{fmtUSD(l.legBusinessUSDT)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </GlassCard>
      )}

      {tab === 'levels' && (
        <GlassCard title="Level Tree · 30 Levels">
          <div className="alert a-info mb-3">
            Click any level to see the members in that level.
          </div>
          {loadingExtra ? (
            <div className="empty-state"><AtomSpinner size="lg" /></div>
          ) : (
            <div className="table-wrap">
              <table className="grx-table clickable-rows">
                <thead>
                  <tr>
                    <th>Level</th>
                    <th>Users</th>
                    <th>Volume</th>
                    <th>Earned</th>
                    <th>%</th>
                    <th>Directs Req</th>
                    <th>Status</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {levels.map((lv, i) => {
                    const count = Number(lv.count);
                    const disabled = count === 0;
                    const earned = levelEarned[i] || 0n;
                    return (
                      <tr
                        key={i}
                        onClick={() => !disabled && setOpenLevel(i + 1)}
                        style={{ cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.55 : 1 }}
                      >
                        <td>L{i + 1}</td>
                        <td className="mono">{String(lv.count)}</td>
                        <td className="mono cyan">{fmtUSD(lv.volumeUSDT)}</td>
                        <td className="mono neon">{fmtUSD(earned)}</td>
                        <td className="mono">{bpsToPct(lv.percentageBps)}</td>
                        <td className="mono">{String(lv.directsRequired)}</td>
                        <td>{lv.eligible ? <span className="badge">Eligible</span> : <span className="badge b-dim">Locked</span>}</td>
                        <td style={{ textAlign: 'right', color: 'var(--grx-text-mute)' }}>
                          {!disabled && <Icon name="arrowRight" size={16} className="dim" />}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </GlassCard>
      )}

      <LevelMembersModal
        rootId={userId}
        level={openLevel}
        onClose={() => setOpenLevel(null)}
      />
    </div>
  );
};

/* ═══════════════════════ LEVEL MEMBERS MODAL ═══════════════════════ */
const LevelMembersModal = ({ rootId, level, onClose }) => {
  const [page, setPage] = useState(0);
  const enabled = !!level;
  const { loading, users, totalAtLevel, truncatedLevels, truncatedDisplay } = useLevelMembers(rootId, level, enabled);

  useEffect(() => { setPage(0); }, [level]);

  if (!level) return null;

  const totalPages = Math.max(1, Math.ceil(users.length / PAGE));
  const shown = users.slice(page * PAGE, page * PAGE + PAGE);

  return (
    <Modal open={!!level} onClose={onClose} title={`Level ${level} · Members`}>
      {loading ? (
        <div className="empty-state">
          <AtomSpinner size="lg" />
          <div className="empty-title mt-3">Fetching Level {level}</div>
          <div className="mt-2 mono" style={{ fontSize: 11, color: 'var(--grx-text-mute)' }}>
            Traversing your team tree — this may take a moment.
          </div>
        </div>
      ) : totalAtLevel === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">◯</div>
          <div className="empty-title">No members yet at this level</div>
        </div>
      ) : (
        <>
          <div className="mono mb-2" style={{ fontSize: 12, color: 'var(--grx-text-dim)' }}>
            Total at L{level}: <span className="text-neon">{totalAtLevel}</span>
            {' · Showing '}
            <span className="text-neon">{users.length}</span>
          </div>

          {(truncatedLevels || truncatedDisplay) && (
            <div className="alert a-warn mb-2" style={{ fontSize: 12 }}>
              {truncatedLevels && 'Tree traversal was capped for safety — deeper levels may show partial results. '}
              {truncatedDisplay && `Only the first ${users.length} members are shown.`}
            </div>
          )}

          <div className="table-wrap" style={{ maxHeight: '52vh', overflowY: 'auto' }}>
            <table className="grx-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Wallet</th>
                  <th>Invested</th>
                  <th>Rank</th>
                  <th>Directs</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((u, i) => (
                  <tr key={i}>
                    <td className="mono">#{String(u.id)}</td>
                    <td className="mono">
                      {truncateAddress(u.wallet)}
                      {u.blacklisted && <span className="badge b-danger" style={{ marginLeft: 6 }}>BL</span>}
                      {!u.blacklisted && !u.active && <span className="badge b-dim" style={{ marginLeft: 6 }}>Inactive</span>}
                    </td>
                    <td className="mono">{fmtUSD(u.totalInvestmentUSDT)}</td>
                    <td className="mono">{rankTag(u.rank)} · <span className="text-dim">{rankLabel(u.rank)}</span></td>
                    <td className="mono">{String(u.directReferrals)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {users.length > PAGE && (
            <div className="pagination">
              <span>Page {page + 1} of {totalPages}</span>
              <div className="pagination-btns">
                <NeonButton size="sm" variant="ghost" onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0}>Prev</NeonButton>
                <NeonButton size="sm" variant="ghost" onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1}>Next</NeonButton>
              </div>
            </div>
          )}
        </>
      )}
    </Modal>
  );
};

export default Team;
